import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  RiSearchLine,
  RiTeamLine,
  RiLoader4Line,
  RiArrowRightSLine,
  RiArrowDownLine,
  RiArrowUpSLine,
  RiArrowDownSLine,
  RiCloseLine,
} from '@remixicon/react';
import Button from '@components/ui/Button';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@store/authStore';
import { normalizeRole, ROLES } from '@/lib/permissions';
import Unauthorized from '@/pages/Unauthorized';
import { Card, CardContent } from '@components/ui/Card';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@components/ui/Table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/Select';
import OrganizationDrawer from '../components/OrganizationDrawer';

// Server-side loader joined onto the org rows so the Fees tab can
// render brackets without a per-row round-trip. We only show the
// tenant's own overrides in this drawer — platform defaults live
// on a separate super-admin surface — so we don't request them.
// `owner` — server-side join so the table can render owner name/email
// directly (no per-row user fetch). `platform_fee_brackets` — same
// pattern for the drawer's Fees tab. Both are safe to keep loaded on
// every page since the response stays flat and small.
const ORG_INCLUDES = ['platform_fee_brackets', 'owner'];

// Load 50 rows per page. Small enough that the initial paint is
// quick, big enough that scanning the list doesn't feel bursty.
const PAGE_SIZE = 50;

const TYPE_OPTIONS = ['BROKER', 'OPERATOR', 'ENTERPRISE', 'INTERNAL'];
const TIER_OPTIONS = ['STARTER', 'PROFESSIONAL', 'ENTERPRISE', 'INTERNAL', 'INACTIVE'];

// Sortable columns exposed to the user. `updated_at` is also a valid
// server sort but no column renders it — omit until we add one.
const SORT_FIELDS = new Set(['created_at', 'name', 'type', 'subscription_tier']);

// Radix `Select` doesn't allow an empty-string item value, so we use
// this sentinel to represent "no filter". Kept out of URL / query state.
const ALL_VALUE = '__all__';

const DEFAULT_SORT_BY = 'created_at';
const DEFAULT_SORT_DIR = 'desc';

// Debounce keystrokes before firing the search query — matches the
// Financials Reports UX.
const SEARCH_DEBOUNCE_MS = 300;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return '—'; }
};

const OrgTypePill = ({ type }) => {
  const isBroker = type === 'BROKER';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full ${
      isBroker
        ? 'bg-indigo-50 text-indigo-700'
        : 'bg-emerald-50 text-emerald-700'
    }`}>
      {type || 'UNKNOWN'}
    </span>
  );
};

// Sortable header cell — click cycles asc → desc → cleared (back to
// default). Matches the header behavior on TaxReports.
const SortableHeader = ({ label, field, sortBy, sortDir, onSort, align = 'left' }) => {
  const active = sortBy === field;
  const arrow = active
    ? (sortDir === 'asc'
        ? <RiArrowUpSLine className="w-3.5 h-3.5" />
        : <RiArrowDownSLine className="w-3.5 h-3.5" />)
    : <RiArrowDownSLine className="w-3.5 h-3.5 opacity-30" />;
  return (
    <TableHead
      className={`!text-${align} !text-[11px] !uppercase !font-semibold !text-slate-500 cursor-pointer select-none`}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {arrow}
      </span>
    </TableHead>
  );
};

const OrganizationsAdminPage = () => {
  const { userProfile } = useAuthStore();
  const normalized = normalizeRole(userProfile?.role);
  const isSuperAdmin = normalized === ROLES.SUPER_ADMIN;

  // URL-persisted query state. All filters, search, and sort round-trip
  // through `?search=…&type=…&subscription_tier=…&sort_by=…&sort_dir=…`
  // so the page is shareable and reload-safe.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearch = searchParams.get('search') || '';
  const urlType = searchParams.get('type') || '';
  const urlTier = searchParams.get('subscription_tier') || '';
  const urlSortBy = SORT_FIELDS.has(searchParams.get('sort_by')) ? searchParams.get('sort_by') : DEFAULT_SORT_BY;
  const urlSortDir = searchParams.get('sort_dir') === 'asc' ? 'asc' : DEFAULT_SORT_DIR;

  // Local input mirror for the search box (typed value). Debounced
  // into the URL — the query key watches the URL, not this input.
  const [searchInput, setSearchInput] = useState(urlSearch);
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  // Keep the input in sync if the URL changes externally (back/forward
  // nav, or another surface writes ?search=…). Skips when the user is
  // actively typing (input already matches the URL update we just made).
  useEffect(() => {
    setSearchInput((prev) => (prev === urlSearch ? prev : urlSearch));
  }, [urlSearch]);

  // Small helper — sets or removes a single param without disturbing
  // the others, and always resets scroll-position-related side effects.
  const setParam = useCallback((key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value == null || value === '' || value === ALL_VALUE) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Debounce `searchInput` → `?search=`. Fires 300 ms after last keystroke.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setParam('search', searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput, setParam]);

  // ── Sort click ─────────────────────────────────────────────────────
  // asc → desc → default (created_at desc). Reused from TaxReports.
  const handleSort = useCallback((field) => {
    if (!SORT_FIELDS.has(field)) return;
    if (urlSortBy !== field) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('sort_by', field);
        next.set('sort_dir', 'asc');
        return next;
      }, { replace: true });
      return;
    }
    if (urlSortDir === 'asc') {
      setParam('sort_dir', 'desc');
      return;
    }
    // was desc → clear back to defaults
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('sort_by');
      next.delete('sort_dir');
      return next;
    }, { replace: true });
  }, [urlSortBy, urlSortDir, setSearchParams, setParam]);

  const clearAllFilters = useCallback(() => {
    setSearchInput('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('search');
      next.delete('type');
      next.delete('subscription_tier');
      // Sort intentionally preserved — clearing filters shouldn't
      // reset the broker's column-sort choice.
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Query params bag consumed by both the query key and the request.
  // Only non-empty / non-default values reach the wire so URL diffs
  // (and the network tab) stay minimal.
  const queryParams = useMemo(() => {
    const p = {};
    if (urlSearch) p.search = urlSearch;
    if (urlType) p.type = urlType;
    if (urlTier) p.subscription_tier = urlTier;
    if (urlSortBy && urlSortBy !== DEFAULT_SORT_BY) p.sort_by = urlSortBy;
    if (urlSortDir && urlSortDir !== DEFAULT_SORT_DIR) p.sort_dir = urlSortDir;
    return p;
  }, [urlSearch, urlType, urlTier, urlSortBy, urlSortDir]);

  // Paginate the org list via `useInfiniteQuery`. Query key includes
  // both `queryParams` and `includes` — any filter/sort/search change
  // resets pagination naturally by cache miss.
  const {
    data: orgPages,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['orgs-admin', { includes: ORG_INCLUDES, ...queryParams }],
    queryFn: async ({ pageParam = 0 }) => {
      const data = await apiClient.getOrganizations({
        skip: pageParam,
        limit: PAGE_SIZE,
        include: ORG_INCLUDES,
        // sort_by / sort_dir are always sent so the server orders
        // consistently even when we're on defaults.
        sort_by: urlSortBy,
        sort_dir: urlSortDir,
        ...queryParams,
      });
      return Array.isArray(data) ? data : [];
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const lastCount = Array.isArray(lastPage) ? lastPage.length : 0;
      if (lastCount < PAGE_SIZE) return undefined;
      return allPages.length * PAGE_SIZE;
    },
    enabled: isSuperAdmin,
    staleTime: 60 * 1000,
  });

  const organizations = useMemo(
    () => orgPages?.pages?.flat() || [],
    [orgPages],
  );

  const selectedOrg = useMemo(
    () => organizations.find((o) => o.organization_id === selectedOrgId) || null,
    [organizations, selectedOrgId],
  );

  // Chip metadata for the active-filters row.
  const activeChips = useMemo(() => {
    const chips = [];
    if (urlType) chips.push({ key: 'type', label: `Type: ${urlType}`, clear: () => setParam('type', '') });
    if (urlTier) chips.push({ key: 'tier', label: `Tier: ${urlTier}`, clear: () => setParam('subscription_tier', '') });
    return chips;
  }, [urlType, urlTier, setParam]);

  // Reject non-super-admins who guess the URL. Sidebar entry is
  // already role-gated but the route itself has no guard beyond
  // ProtectedRoute's auth check.
  if (userProfile && !isSuperAdmin) {
    return <Unauthorized />;
  }

  const hasAnyFilter = !!(urlSearch || urlType || urlTier);

  return (
    <div className="max-w-6xl px-4 md:px-6 py-6">
      {/* Search + filter controls. Page title is already rendered by the
          top-nav / AppLayout — no redundant header block here. Search input
          uses the Financials pill styling (rounded-full, inline clear ×). */}
      <div className="flex flex-col md:flex-row md:items-center gap-2 mb-4">
        <div className="flex-1 min-w-[200px] max-w-md relative">
          <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, billing email, tax id…"
            className="w-full pl-9 pr-9 py-2 bg-white border border-gray-200 rounded-full text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <RiCloseLine className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Select
            value={urlType || ALL_VALUE}
            onValueChange={(v) => setParam('type', v)}
          >
            <SelectTrigger className="h-10 min-w-[9rem] bg-white !border !border-gray-200 !rounded-full text-sm px-4 focus:ring-2 focus:ring-primary-500 focus:border-transparent">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent className="!bg-white">
              <SelectItem value={ALL_VALUE}>All types</SelectItem>
              {TYPE_OPTIONS.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={urlTier || ALL_VALUE}
            onValueChange={(v) => setParam('subscription_tier', v)}
          >
            <SelectTrigger className="h-10 min-w-[10rem] bg-white !border !border-gray-200 !rounded-full text-sm px-4 focus:ring-2 focus:ring-primary-500 focus:border-transparent">
              <SelectValue placeholder="All tiers" />
            </SelectTrigger>
            <SelectContent className="!bg-white">
              <SelectItem value={ALL_VALUE}>All tiers</SelectItem>
              {TIER_OPTIONS.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Active-filter chips row. Hidden when no chip filter is set. */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 px-1">
          {activeChips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={chip.clear}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-[12px] font-medium text-slate-700 transition-colors"
            >
              {chip.label}
              <RiCloseLine className="w-3.5 h-3.5" />
            </button>
          ))}
          {hasAnyFilter && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-50 hover:bg-rose-100 text-[12px] font-semibold text-rose-600 transition-colors"
            >
              <RiCloseLine className="w-3.5 h-3.5" />
              Clear all
            </button>
          )}
        </div>
      )}

      <Card className="border border-slate-200 bg-white rounded-2xl overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/70">
                  <SortableHeader label="Name" field="name" sortBy={urlSortBy} sortDir={urlSortDir} onSort={handleSort} />
                  <SortableHeader label="Type" field="type" sortBy={urlSortBy} sortDir={urlSortDir} onSort={handleSort} />
                  <TableHead className="!text-left !text-[11px] !uppercase !font-semibold !text-slate-500">Owner</TableHead>
                  <TableHead className="!text-left !text-[11px] !uppercase !font-semibold !text-slate-500">Members</TableHead>
                  <SortableHeader label="Subscription" field="subscription_tier" sortBy={urlSortBy} sortDir={urlSortDir} onSort={handleSort} />
                  <SortableHeader label="Created" field="created_at" sortBy={urlSortBy} sortDir={urlSortDir} onSort={handleSort} />
                  <TableHead className="!text-right !text-[11px] !uppercase !font-semibold !text-slate-500"> </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-sm text-slate-500">
                      <RiLoader4Line className="w-5 h-5 animate-spin inline-block mr-2 text-primary-500" />
                      Loading organizations…
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && organizations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-sm text-slate-500">
                      {hasAnyFilter ? (
                        <div className="flex flex-col items-center gap-3">
                          <span>No matches. Try a different search or clear filters.</span>
                          <button
                            type="button"
                            onClick={clearAllFilters}
                            className="text-sm font-medium text-primary-600 hover:text-primary-700"
                          >
                            Clear filters
                          </button>
                        </div>
                      ) : 'No organizations yet.'}
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && organizations.map((org) => (
                  <TableRow
                    key={org.organization_id}
                    onClick={() => setSelectedOrgId(org.organization_id)}
                    className="hover:bg-slate-50/60 cursor-pointer transition-colors"
                  >
                    <TableCell className="!text-left">
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-slate-900 truncate max-w-[240px]">
                          {org.name || '—'}
                        </span>
                        {org.billing_email && (
                          <span className="text-xs text-slate-500 truncate max-w-[240px]">
                            {org.billing_email}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="!text-left">
                      <OrgTypePill type={org.type} />
                    </TableCell>
                    <TableCell className="!text-left text-sm text-slate-700">
                      {org.owner ? (
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-slate-900 truncate max-w-[200px]">
                            {[org.owner.first_name, org.owner.last_name].filter(Boolean).join(' ') || org.owner.email || '—'}
                          </span>
                          {org.owner.email && (
                            <span className="text-xs text-slate-500 truncate max-w-[200px]">
                              {org.owner.email}
                            </span>
                          )}
                        </div>
                      ) : org.owned_by ? (
                        /* An owner is assigned (owned_by is set) but the
                           owner profile didn't come back on the include —
                           API race, permission scope, or deleted user.
                           Neutral "Has owner" instead of the alarming
                           amber "Not set". Open the drawer to load the
                           user directly via getUser(owned_by). */
                        <span className="text-xs text-slate-500 italic">
                          Has owner
                        </span>
                      ) : (
                        <span className="text-xs text-amber-600 font-medium">Not set</span>
                      )}
                    </TableCell>
                    <TableCell className="!text-left text-sm text-slate-700">
                      <span className="inline-flex items-center gap-1">
                        <RiTeamLine className="w-3.5 h-3.5 text-slate-400" />
                        {org.member_count ?? 0}
                      </span>
                    </TableCell>
                    <TableCell className="!text-left text-sm text-slate-700">
                      {org.subscription_tier || '—'}
                    </TableCell>
                    <TableCell className="!text-left text-sm text-slate-600">
                      {formatDate(org.created_at)}
                    </TableCell>
                    <TableCell className="!text-right pr-4">
                      <RiArrowRightSLine className="w-4 h-4 text-slate-400 inline-block" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-3 px-1">
        <span className="text-xs text-slate-500">
          {isFetching && !isLoading && !isFetchingNextPage
            ? 'Refreshing…'
            : `${organizations.length} loaded`}
          {hasNextPage && ' · more available'}
        </span>

        {hasNextPage && (
          <Button
            type="button"
            variant="secondary"
            mode="stroke"
            className="!font-normal !border-slate-200 hover:!bg-slate-50 rounded-full text-sm"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? (
              <span className="inline-flex items-center gap-2">
                <RiLoader4Line className="w-4 h-4 animate-spin" />
                Loading…
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <RiArrowDownLine className="w-4 h-4" />
                Load more
              </span>
            )}
          </Button>
        )}
      </div>

      <OrganizationDrawer
        organization={selectedOrg}
        open={!!selectedOrg}
        onClose={() => setSelectedOrgId(null)}
        // Drawer mutations write directly to the orgs-admin cache
        // (see features/organizations-admin/utils.js), so no refetch
        // is needed here — the table + selectedOrg update in place.
      />
    </div>
  );
};

export default OrganizationsAdminPage;
